import { useEffect, useMemo, useState } from "react";
import { useToast } from "@chakra-ui/react";
import axios from "../../../axiosConfig";

const normalizeItems = (items) => (Array.isArray(items) ? items : []);

const buildProgressMap = (records) => {
  if (!Array.isArray(records)) return {};

  return records.reduce((acc, record) => {
    acc[record.checklist_item_id] = record.completed;
    return acc;
  }, {});
};

const useWorkbookChecklistProgress = ({
  interpretationId,
  organizationSurveyId,
  fallbackItems = [],
  readOnly = false,
}) => {
  const toast = useToast();
  const [items, setItems] = useState(normalizeItems(fallbackItems));
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setItems(normalizeItems(fallbackItems));
  }, [fallbackItems]);

  useEffect(() => {
    let active = true;

    const loadChecklist = async () => {
      if (!interpretationId || !organizationSurveyId) {
        setHasLoaded(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setHasLoaded(false);
        setError("");

        const [itemsResponse, progressResponse] = await Promise.all([
          axios.get(`/interpretations/${interpretationId}/checklist_items`, {
            params: { organization_survey_id: organizationSurveyId },
          }),
          axios.get(`/interpretations/${interpretationId}/checklist_progresses`, {
            params: { organization_survey_id: organizationSurveyId },
          }),
        ]);

        if (!active) return;
        setItems(normalizeItems(itemsResponse.data));
        setProgress(buildProgressMap(progressResponse.data));
      } catch (err) {
        if (!active) return;
        setError("Could not load checklist progress.");
      } finally {
        if (active) {
          setHasLoaded(true);
          setLoading(false);
        }
      }
    };

    loadChecklist();

    return () => {
      active = false;
    };
  }, [interpretationId, organizationSurveyId]);

  const toggleItem = async (itemId, completed) => {
    if (readOnly) {
      toast({
        title: "Read-only workbook",
        description: "Checklist progress cannot be changed in read-only mode.",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!interpretationId || !organizationSurveyId) {
      toast({
        title: "Missing school context",
        description: "Cannot save checklist progress for this workbook item.",
        status: "warning",
      });
      return;
    }

    setProgress((prev) => ({ ...prev, [itemId]: completed }));
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, completed } : item
      )
    );

    try {
      await axios.post(`/interpretations/${interpretationId}/checklist_progresses`, {
        checklist_progress: {
          organization_survey_id: organizationSurveyId,
          checklist_item_id: itemId,
          completed,
        },
      });

      toast({
        title: "Progress saved",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (err) {
      setProgress((prev) => ({ ...prev, [itemId]: !completed }));
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, completed: !completed } : item
        )
      );
      toast({
        title: "Failed to save progress",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const completedCount = useMemo(
    () =>
      items.filter((item) => {
        const itemId = item.id;
        return progress[itemId] ?? item.completed;
      }).length,
    [items, progress]
  );

  const totalCount = items.length;
  const percentComplete =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    items,
    progress,
    loading,
    hasLoaded,
    error,
    completedCount,
    totalCount,
    percentComplete,
    toggleItem,
    readOnly,
  };
};

export default useWorkbookChecklistProgress;
